import { useCheckoutContext } from '../index'
import type { Validate } from '@ztechapps/common.hook'
import { useError, useMutation } from '@ztechapps/common.hook'
import { orderFulfillPaymentQuery } from '../graphql'
import isEmpty from 'lodash/isEmpty'
import merge from 'lodash/merge'
import type { PaymentOptions } from '../types'
import { PaymentMethodEnum } from '@ztechapps/common.contract'
import { usePaymentValidation } from './usePaymentValidation'

export const useStartFreePayment = (validate?: Validate<PaymentOptions>) => {
  const checkoutContext = useCheckoutContext()
  const fromCheckout = checkoutContext.fromCheckout
  const { document } = checkoutContext.orderDocumentResult
  const orderNetAmount = checkoutContext.orderNetAmount
  const { hasError } = checkoutContext.checkoutErrors
  const [, setShowLoadingOverlay] = checkoutContext.showLoadingOverlay
  const { onError, error } = useError()
  const [paymentOptions] = checkoutContext.paymentOptions

  const [fulfill, ,] = useMutation({ query: orderFulfillPaymentQuery })
  const partialPaymentValidation = usePaymentValidation()

  const internalValidate: Validate<PaymentOptions> = (args?: PaymentOptions) => {
    //do the validation check if it was provided
    const validateErrors = validate?.(args) ?? {}
    const paymentValidationErrors = partialPaymentValidation(args)

    const freePaymentValidationErrors: Record<any, unknown> = {}

    if (isEmpty(args.remarks)) {
      freePaymentValidationErrors.remarks = 'Required'
    }

    return merge({}, paymentValidationErrors, validateErrors, freePaymentValidationErrors)
  }

  const onStartPayment = async (args?: PaymentOptions) => {
    if (hasError()) return

    try {
      const paymentRemarks = `${args?.remarks ?? paymentOptions.remarks}`
      const paymentAmount = args?.amount ?? paymentOptions.amount
      setShowLoadingOverlay(true)

      const orderId = document?._id

      //this should set the net amount as the discount.
      //the payment will be cash.

      await fulfill({
        input: {
          _id: orderId,
          orderId,
          fulfilledVia: 'FREE PAYMENT',
          paymentMethod: PaymentMethodEnum.free, //still the payment method is cash
          remarks: paymentRemarks,
          paymentNet: paymentAmount ?? orderNetAmount,
          paymentGross: paymentAmount ?? orderNetAmount,
          fromCheckout,
        },
      })

      await checkoutContext?.onSuccess?.()
    } catch (e) {
      onError(e)
      throw e
    } finally {
      setShowLoadingOverlay(false)
    }
  }

  return {
    onStartPayment,
    hasError: !isEmpty(error),
    error,
    validate: internalValidate,
  }
}
